import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  ApiError,
  fetchContractTypes,
  streamReview,
  typesWithApprovedRules,
  type ContractTypeOption,
} from '../api'
import type {
  CheckStatus,
  ClauseStartedEvent,
  Finding,
  FindingType,
  Importance,
  PlaybookCheck,
  ReviewCompleteEvent,
  ReviewErrorEvent,
  ReviewStartedEvent,
  ReviewStats,
} from '../types'
import { getBodyOoxml, locateAndSelect } from '../word'
import { FindingCard } from './FindingCard'
import {
  checkCanNavigate,
  checkLocationLabel,
  checkStatusLabel,
  checkReasonLabel,
  countByImportance,
  formatReviewReport,
  importanceLabel,
  IMPORTANCE_ORDER,
  findingImportance,
  isBlankPlaybookCheck,
  isRecurring,
  typeLabel,
} from '../findings'
import { streamingProgressCopy } from '../reviewProgress'
import { t } from '../i18n'

type Phase =
  | { kind: 'idle' }
  | { kind: 'reading' }
  | { kind: 'uploading' }
  | {
      kind: 'streaming'
      clauseCount: number | null
      clauseInProgress: ClauseStartedEvent | null
      started: number
    }
  | { kind: 'complete'; stats: ReviewStats; message?: string }
  | { kind: 'error'; message: string; code?: string }

const ERROR_MESSAGES: Record<string, string> = {
  empty_playbook: 'Немає погоджених правил цього типу. Погодьте правило в Playbook.',
  empty_playbook_excluded:
    'Погоджені правила виключені з порівняння. Увімкніть «Порівнювати» в Playbook.',
  quota_exceeded: 'Ліміт ревʼю на цьому тарифі вичерпано. Оновіть план у кабінеті.',
  too_many_reviews: 'Уже виконується ревʼю. Дочекайтеся завершення.',
  validation_error: 'Не вдалося розібрати документ.',
  client_disconnected: 'Зʼєднання розірвалось.',
}

function errorMessage(code: string | undefined): string | undefined {
  const message = code ? ERROR_MESSAGES[code] : undefined
  return message ? t(message) : undefined
}

// Повтор — це ознака знахідки, а не окремий тип: картка лишається у своїй групі.
type GroupType = Exclude<FindingType, 'recurring'>
type ReviewTab = 'fixes' | 'atypical' | 'playbook'
const FIX_TYPES: GroupType[] = ['deviation', 'missing']
type Filter = GroupType | 'all' | 'recurring'

function isFixType(type: FindingType): boolean {
  return type === 'deviation' || type === 'missing'
}

interface Props {
  token: string
  onLogout: () => void
  onFindingsChange?: (findings: Finding[]) => void
  rerunNonce?: number
  onRunningChange?: (running: boolean) => void
}

export function ReviewPanel({
  token,
  onLogout,
  onFindingsChange,
  rerunNonce = 0,
  onRunningChange,
}: Props) {
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })
  const [findings, setFindings] = useState<Finding[]>([])
  const [reviewId, setReviewId] = useState<string | null>(null)
  const [commented, setCommented] = useState<Set<string>>(new Set())
  const [applied, setApplied] = useState<Set<string>>(new Set())
  const [ignored, setIgnored] = useState<Set<string>>(new Set())
  const [filter, setFilter] = useState<Filter>('all')
  const [importanceFilter, setImportanceFilter] = useState<Importance | 'all'>('all')
  const [checkFilter, setCheckFilter] = useState<'all' | CheckStatus>(
    'all',
  )
  const [tab, setTab] = useState<ReviewTab>('fixes')
  const [contractTypes, setContractTypes] = useState<ContractTypeOption[]>([])
  const [contractType, setContractType] = useState('')
  const [replaceConfirm, setReplaceConfirm] = useState(false)
  const [playbookChecks, setPlaybookChecks] = useState<PlaybookCheck[]>([])

  const [stale, setStale] = useState(false)
  const reviewedText = useRef<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const startRef = useRef<(opts?: { replace?: boolean }) => Promise<void>>(
    async () => undefined,
  )
  const isRunning =
    phase.kind === 'reading' || phase.kind === 'uploading' || phase.kind === 'streaming'

  const loadContractTypes = useCallback(() => {
    fetchContractTypes(token)
      .then(setContractTypes)
      .catch((err) => {
        // Non-fatal: the review still runs against the whole playbook.
        console.warn('contract types load failed', err)
      })
  }, [token])

  useEffect(() => {
    loadContractTypes()
  }, [loadContractTypes])

  const approvedTypes = useMemo(() => typesWithApprovedRules(contractTypes), [contractTypes])
  const selectedType = approvedTypes.some((t) => t.value === contractType) ? contractType : ''
  const rulesInScope = useMemo(() => {
    if (!selectedType) return approvedTypes.reduce((sum, t) => sum + t.ruleCount, 0)
    return approvedTypes.find((t) => t.value === selectedType)?.ruleCount ?? 0
  }, [selectedType, approvedTypes])
  const unapprovedInScope = useMemo(() => {
    if (!selectedType) return contractTypes.reduce((sum, t) => sum + t.unapprovedCount, 0)
    return contractTypes.find((t) => t.value === selectedType)?.unapprovedCount ?? 0
  }, [selectedType, contractTypes])
  const excludedInScope = useMemo(() => {
    const rows = selectedType
      ? contractTypes.filter((type) => type.value === selectedType)
      : contractTypes
    return rows.reduce((sum, type) => sum + type.excludedCount, 0)
  }, [selectedType, contractTypes])

  const openFindings = useMemo(
    () => findings.filter((f) => f.verdict !== 'ok'),
    [findings],
  )
  const fixFindings = useMemo(
    () => openFindings.filter((f) => isFixType(f.finding_type)),
    [openFindings],
  )
  const atypicalFindings = useMemo(
    () => openFindings.filter((f) => f.finding_type === 'atypical'),
    [openFindings],
  )

  const visibleFixes = useMemo(
    () =>
      fixFindings.filter((f) => {
        if (ignored.has(findingKey(f))) return false
        if (filter === 'recurring' && !isRecurring(f)) return false
        if (filter !== 'all' && filter !== 'recurring' && f.finding_type !== filter) {
          return false
        }
        if (importanceFilter !== 'all' && findingImportance(f) !== importanceFilter) {
          return false
        }
        return true
      }),
    [fixFindings, ignored, filter, importanceFilter],
  )

  const visibleAtypical = useMemo(
    () =>
      atypicalFindings.filter((f) => {
        if (ignored.has(findingKey(f))) return false
        if (importanceFilter !== 'all' && findingImportance(f) !== importanceFilter) {
          return false
        }
        return true
      }),
    [atypicalFindings, ignored, importanceFilter],
  )

  const fixImportanceCounts = useMemo(
    () =>
      countByImportance(fixFindings.filter((f) => !ignored.has(findingKey(f)))),
    [fixFindings, ignored],
  )
  const atypicalImportanceCounts = useMemo(
    () =>
      countByImportance(atypicalFindings.filter((f) => !ignored.has(findingKey(f)))),
    [atypicalFindings, ignored],
  )

  const openCount = fixFindings.length
  const solvedCount = fixFindings.filter(
    (f) => applied.has(findingKey(f)) || ignored.has(findingKey(f)),
  ).length
  const fixCount = fixFindings.filter((f) => !ignored.has(findingKey(f))).length
  const atypicalCount = atypicalFindings.filter((f) => !ignored.has(findingKey(f))).length
  const hasResult =
    phase.kind === 'complete' || findings.length > 0 || playbookChecks.length > 0
  const hasFixFindings = fixCount > 0
  const hasAtypicalFindings = atypicalCount > 0

  const listedChecks = useMemo(
    () => playbookChecks.filter((check) => !isBlankPlaybookCheck(check)),
    [playbookChecks],
  )
  const visibleChecks = useMemo(() => {
    if (checkFilter === 'all') return listedChecks
    return listedChecks.filter((c) => c.status === checkFilter)
  }, [listedChecks, checkFilter])

  const groupedFixes = useMemo(() => groupByType(visibleFixes), [visibleFixes])
  const groupedAtypical = useMemo(() => groupByType(visibleAtypical), [visibleAtypical])

  useEffect(() => {
    if (phase.kind !== 'complete' || stale) return
    let disposed = false
    let pending = false
    const timer = window.setInterval(async () => {
      if (pending) return
      pending = true
      try {
        const current = documentText(await getBodyOoxml())
        if (!disposed && reviewedText.current !== null && current !== reviewedText.current) setStale(true)
      } catch { /* Office may be unavailable while Word is closing. */ }
      finally { pending = false }
    }, 5000)
    return () => { disposed = true; window.clearInterval(timer) }
  }, [phase.kind, stale])

  const start = useCallback(async (opts?: { replace?: boolean }) => {
    if (isRunning) return
    if (findings.length > 0 && !opts?.replace) {
      setReplaceConfirm(true)
      return
    }

    setReplaceConfirm(false)
    setFindings([])
    setCommented(new Set())
    setApplied(new Set())
    setIgnored(new Set())
    setReviewId(null)
    setPlaybookChecks([])
    setTab('fixes')
    setFilter('all')
    setImportanceFilter('all')
    setCheckFilter('all')
    setStale(false)
    setPhase({ kind: 'reading' })
    loadContractTypes()

    const controller = new AbortController()
    abortRef.current = controller
    const startedAt = performance.now()
    let firstFindingAt: number | null = null

    let ooxml: string
    try {
      ooxml = await getBodyOoxml()
      reviewedText.current = documentText(ooxml)
    } catch (err) {
      console.error('getOoxml failed', err)
      setPhase({
        kind: 'error',
        message:
          t('Не вдалося прочитати документ. Збережіть файл і вимкніть «Захищений режим», якщо він увімкнений.'),
      })
      return
    }

    setPhase({ kind: 'uploading' })

    try {
      await streamReview({
        token,
        ooxml,
        ...(selectedType ? { contract_type: selectedType } : {}),
        signal: controller.signal,
        onEvent: (evt) => {
          const data = safeParse(evt.data)
          switch (evt.event) {
            case 'review_started': {
              const started = data as ReviewStartedEvent
              setReviewId(started?.review_id ?? null)
              setPhase({
                kind: 'streaming',
                clauseCount: started?.clause_count ?? null,
                clauseInProgress: null,
                started: 0,
              })
              break
            }
            case 'clause_started': {
              const clause = data as ClauseStartedEvent
              setPhase((p) =>
                p.kind === 'streaming'
                  ? {
                      ...p,
                      clauseInProgress: clause,
                      started: clause.done ?? p.started + 1,
                    }
                  : p,
              )
              break
            }
            case 'finding': {
              const finding = normalizeFinding(data as Finding)
              if (firstFindingAt === null) {
                firstFindingAt = performance.now() - startedAt
                if (import.meta.env.DEV) {
                  console.info(`[juniverso] TTFF ${firstFindingAt.toFixed(0)} ms`)
                }
              }
              setFindings((prev) => [...prev, finding])
              break
            }
            case 'review_complete': {
              const done = (data ?? {}) as ReviewCompleteEvent
              const stats = done.stats ?? {}
              if (Array.isArray(done.findings) && done.findings.length > 0) {
                setFindings(done.findings.map(normalizeFinding))
              }
              if (Array.isArray(stats.playbook_checks)) {
                setPlaybookChecks(stats.playbook_checks.map(normalizePlaybookCheck))
              }
              setPhase({ kind: 'complete', stats })
              break
            }
            case 'error': {
              const err = (data ?? {}) as ReviewErrorEvent
              setPhase({
                kind: 'error',
                message: errorMessage(err.code) ?? err.message ?? t('Помилка ревʼю.'),
                code: err.code,
              })
              break
            }
            default:
              break
          }
        },
      })
      setPhase((p) => (p.kind === 'streaming' ? { kind: 'complete', stats: {} } : p))
    } catch (err) {
      if (controller.signal.aborted) return
      if (err instanceof ApiError && err.status === 401) {
        onLogout()
        return
      }
      const status = err instanceof ApiError ? err.status : undefined
      const code = err instanceof ApiError ? err.code : undefined
      const message =
        status === 413
          ? t('Документ завеликий (ліміт 20 МБ).')
          : status === 415
            ? t('Формат документа не підтримується.')
            : status === 429
              ? (errorMessage(code) ??
                t('Ліміт ревʼю вичерпано або надто багато одночасних запитів.'))
              : err instanceof Error
                ? err.message
                : t('Мережева помилка.')
      setPhase({ kind: 'error', message, code })
    } finally {
      abortRef.current = null
    }
  }, [selectedType, findings.length, isRunning, loadContractTypes, onLogout, token])

  useLayoutEffect(() => {
    startRef.current = start
  }, [start])

  useEffect(() => {
    onRunningChange?.(isRunning)
  }, [isRunning, onRunningChange])

  useEffect(() => {
    onFindingsChange?.(findings)
  }, [findings, onFindingsChange])

  useEffect(() => {
    if (!rerunNonce) return
    void startRef.current({ replace: true })
  }, [rerunNonce])

  function cancel() {
    abortRef.current?.abort()
    setPhase({ kind: 'idle' })
  }

  function downloadReport() {
    const md = formatReviewReport({
      findings: findings.filter((f) => f.verdict !== 'ok'),
      checks: listedChecks,
      stats: phase.kind === 'complete' ? phase.stats : {},
    })
    const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'eudokia-review.md'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <>
      <section className="card review-setup">
        <label>{t('Тип договору')}<select
            value={selectedType}
            onChange={(e) => setContractType(e.target.value)}
            disabled={isRunning}
          >
            <option value="">{t('Усі типи')}</option>
            {approvedTypes.map((t) => (
              <option key={t.value} value={t.value}>
                {t.value} ({t.ruleCount})
              </option>
            ))}
          </select>
        </label>

        <p className="muted small">
          {rulesInScope === 0
            ? excludedInScope > 0
              ? t('Погоджені правила виключені з порівняння. Увімкніть «Порівнювати» в Playbook.')
              : unapprovedInScope > 0
                ? t('Немає погоджених правил цього типу. Погодьте правило в Playbook.')
                : t('Плейбук порожній — додайте правила в кабінеті.')
            : t('Буде застосовано правил: {n}.', { n: rulesInScope })}
        </p>
        {unapprovedInScope > 0 ? (
          <p className="muted small">
            {selectedType
              ? t('У цьому типі {n} непогоджених правил. Їх не враховано.', { n: unapprovedInScope })
              : t('У плейбуку {n} непогоджених правил. Їх не враховано.', { n: unapprovedInScope })}
          </p>
        ) : null}

        <div className="actions">
          <button type="button" onClick={() => void start()} disabled={isRunning || rulesInScope === 0}>
            {isRunning ? t('Виконується…') : hasResult ? t('Перевірити знову') : t('Перевірити')}
          </button>
          {isRunning ? (
            <button type="button" className="secondary" onClick={cancel}>{t('Скасувати')}</button>
          ) : null}
          {hasResult && !isRunning ? (
            <button type="button" className="secondary" onClick={downloadReport}>{t('Звіт')}</button>
          ) : null}
        </div>
      </section>

      <PhaseBanner phase={phase} />

      {hasResult ? (
        <>
          <nav className="review-tabs" aria-label={t('Шар результату')} role="tablist">
            <button
              type="button"
              role="tab"
              className={tab === 'fixes' ? 'active' : ''}
              aria-selected={tab === 'fixes'}
              onClick={() => setTab('fixes')}
            >{t('Правки')}<span className="count">{fixCount}</span>
            </button>
            <button
              type="button"
              role="tab"
              className={tab === 'atypical' ? 'active' : ''}
              aria-selected={tab === 'atypical'}
              onClick={() => setTab('atypical')}
            >{t('Нетипове')}<span className="count">{atypicalCount}</span>
            </button>
            <button
              type="button"
              role="tab"
              className={tab === 'playbook' ? 'active' : ''}
              aria-selected={tab === 'playbook'}
              onClick={() => setTab('playbook')}
            >{t('Плейбук')}<span className="count">{listedChecks.length}</span>
            </button>
          </nav>

          <div className="review-tab" hidden={tab !== 'fixes'}>
            <p className="tab-hint muted small">
              {t('Що треба виправити в цьому файлі: порушення або відсутні умови плейбука.')}
            </p>
            {openCount > 0 ? (
              <section className="card alerts-summary">
                <h2>
                  {t('Сповіщення ({solved}/{open} вирішено)', {
                    solved: Math.min(solvedCount, openCount),
                    open: openCount,
                  })}
                </h2>
                <ImportanceChips
                  value={importanceFilter}
                  counts={fixImportanceCounts}
                  onChange={setImportanceFilter}
                />
              </section>
            ) : null}
            {hasFixFindings ? (
              <div className="filters">
                <button
                  type="button"
                  className={filter === 'all' ? 'chip active' : 'chip'}
                  onClick={() => setFilter('all')}
                >{t('Усі')}</button>
                {FIX_TYPES.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={filter === t ? `chip active chip-${t}` : `chip chip-${t}`}
                    onClick={() => setFilter(t)}
                  >
                    {typeLabel(t)}
                  </button>
                ))}
                {fixFindings.some(isRecurring) ? (
                  <button
                    type="button"
                    className={
                      filter === 'recurring'
                        ? 'chip active chip-recurring'
                        : 'chip chip-recurring'
                    }
                    onClick={() => setFilter('recurring')}
                  >{t('Повтор')}</button>
                ) : null}
              </div>
            ) : null}
            {visibleFixes.length > 0 ? (
              <FindingsList
                types={FIX_TYPES}
                grouped={groupedFixes}
                token={token}
                reviewId={reviewId}
                commented={commented}
                applied={applied}
                onCommentInserted={(key) => setCommented((prev) => withKey(prev, key))}
                onApplied={(key) => { setApplied((prev) => withKey(prev, key)); setStale(true) }}
                onIgnored={(key) => setIgnored((prev) => withKey(prev, key))}
              />
            ) : hasFixFindings ? (
              <p className="muted">{t('Немає знахідок для цього фільтра.')}</p>
            ) : (
              <p className="muted">
                {t(
                  'Порушень плейбука не знайдено. Нетипове: {n}, покриття правил — у вкладці «Плейбук».',
                  { n: atypicalCount },
                )}
              </p>
            )}
          </div>

          <div className="review-tab" hidden={tab !== 'atypical'}>
            <p className="tab-hint muted small">
              {t('Умови, яких немає у вашому плейбуку. Це не порушення — просто звернути увагу.')}
            </p>
            {hasAtypicalFindings ? (
              <ImportanceChips
                value={importanceFilter}
                counts={atypicalImportanceCounts}
                onChange={setImportanceFilter}
              />
            ) : null}
            {visibleAtypical.length > 0 ? (
              <FindingsList
                types={['atypical']}
                grouped={groupedAtypical}
                token={token}
                reviewId={reviewId}
                commented={commented}
                applied={applied}
                onCommentInserted={(key) => setCommented((prev) => withKey(prev, key))}
                onApplied={(key) => { setApplied((prev) => withKey(prev, key)); setStale(true) }}
                onIgnored={(key) => setIgnored((prev) => withKey(prev, key))}
              />
            ) : hasAtypicalFindings ? (
              <p className="muted">{t('Немає знахідок для цього фільтра.')}</p>
            ) : (
              <p className="muted">{t('Нетипових умов не знайдено.')}</p>
            )}
          </div>

          <div className="review-tab" hidden={tab !== 'playbook'}>
            <p className="tab-hint muted small">
              {t('Чи знайшлось у документі підтвердження кожного правила. Це не список правок.')}
            </p>
            {listedChecks.length > 0 ? (
              <div className="filters">
                <button
                  type="button"
                  className={checkFilter === 'all' ? 'chip active' : 'chip'}
                  onClick={() => setCheckFilter('all')}
                >
                  {t('Усі ({n})', { n: listedChecks.length })}
                </button>
                {(['unmet', 'partial', 'met', 'not_applicable', 'needs_review', 'error', 'not_evaluated'] as CheckStatus[]).map(status => (
                  <button type="button" key={status}
                    className={checkFilter === status ? 'chip active' : 'chip'}
                    onClick={() => setCheckFilter(status)}>
                    {checkStatusLabel(status)} ({listedChecks.filter(c => c.status === status).length})
                  </button>
                ))}
              </div>
            ) : null}
            {visibleChecks.length > 0 ? (
              <ul className="check-list">
                {visibleChecks.map((c) => (
                  <PlaybookCheckRow key={c.rule_id} check={c} />
                ))}
              </ul>
            ) : listedChecks.length > 0 ? (
              <p className="muted">{t('Немає правил для цього фільтра.')}</p>
            ) : (
              <p className="muted">{t('Немає правил плейбука в цьому ревʼю.')}</p>
            )}
          </div>
        </>
      ) : null}

      {stale ? <p role="alert" className="warn">{t('Документ змінився. Результати застаріли — запустіть ревю повторно.')}</p> : null}
      {replaceConfirm
        ? createPortal(
            <div
              className="catalog-dialog-backdrop"
              onClick={() => setReplaceConfirm(false)}
            >
              <div
                className="catalog-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="replace-review-title"
                onClick={(e) => e.stopPropagation()}
              >
                <p id="replace-review-title">{t('Замінити попередній результат новим ревʼю?')}</p>
                <div className="actions">
                  <button type="button" onClick={() => void start({ replace: true })}>{t('Перевірити знову')}</button>
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => setReplaceConfirm(false)}
                  >{t('Скасувати')}</button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  )
}

function useElapsedSeconds(running: boolean): number {
  const [sec, setSec] = useState(0)
  useEffect(() => {
    if (!running) {
      setSec(0)
      return
    }
    setSec(0)
    const id = window.setInterval(() => setSec((n) => n + 1), 1000)
    return () => window.clearInterval(id)
  }, [running])
  return sec
}

function PhaseBanner({ phase }: { phase: Phase }) {
  const running =
    phase.kind === 'reading' || phase.kind === 'uploading' || phase.kind === 'streaming'
  const elapsedSec = useElapsedSeconds(running)

  switch (phase.kind) {
    case 'idle':
      return (
        <p className="muted">
          {t(
            'Натисніть «Перевірити», щоб звірити документ із плейбуком (відхилення, відсутні положення, нетипове, повтори).',
          )}
        </p>
      )
    case 'reading':
      return (
        <div className="progress-block">
          <p className="progress">{t('Читаю документ…')}</p>
          <div className="progress-bar is-waiting" role="progressbar" aria-label={t('Читаю документ')}>
            <span />
          </div>
        </div>
      )
    case 'uploading':
      return (
        <div className="progress-block">
          <p className="progress">{t('Надсилаю на сервер…')}</p>
          <div
            className="progress-bar is-waiting"
            role="progressbar"
            aria-label={t('Надсилаю на сервер')}
          >
            <span />
          </div>
        </div>
      )
    case 'streaming': {
      const total = phase.clauseCount
      const ratio =
        total != null && total > 0 ? Math.min(1, phase.started / total) : null
      const { title, hint } = streamingProgressCopy({
        done: phase.started,
        total,
        elapsedSec,
      })
      const waiting = ratio == null || phase.started === 0
      return (
        <div className="progress-block">
          <p className="progress">{title}</p>
          <div
            className={waiting ? 'progress-bar is-waiting' : 'progress-bar is-busy'}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={waiting ? undefined : Math.round((ratio ?? 0) * 100)}
            aria-label={title}
          >
            <span style={waiting ? undefined : { width: `${Math.round((ratio ?? 0) * 100)}%` }} />
          </div>
          <p className="progress-hint">{hint}</p>
        </div>
      )
    }
    case 'complete': {
      const s = phase.stats
      const summary =
        phase.message ??
        t('Готово. Відхилень: {t1}, відсутніх: {t2}, нетипових: {t3}, повторів: {t4}.', {
          t1: s.t1 ?? 0,
          t2: s.t2 ?? 0,
          t3: s.t3 ?? 0,
          t4: s.t4 ?? 0,
        })
      return <p className={s.review_complete === false ? 'warn' : 'ok'}>
        {s.review_complete === false
          ? `${t('Ревю неповне. Перегляньте неперевірені правила у вкладці «Плейбук».')} `
          : ''}
        {summary}
      </p>
    }
    case 'error':
      return <p className="error">{phase.message}</p>
  }
}

interface ListProps {
  types: GroupType[]
  grouped: Record<GroupType, Finding[]>
  token: string
  reviewId: string | null
  commented: Set<string>
  applied: Set<string>
  onCommentInserted: (key: string) => void
  onApplied: (key: string) => void
  onIgnored: (key: string) => void
}

function FindingsList({
  types,
  grouped,
  token,
  reviewId,
  commented,
  applied,
  onCommentInserted,
  onApplied,
  onIgnored,
}: ListProps) {
  return (
    <div className="findings">
      {types.map((type) => {
        const list = grouped[type]
        if (list.length === 0) return null
        return (
          <section key={type} className="group">
            <h2 className="group-head">
              <span className={`verdict type-${type}`}>{typeLabel(type)}</span>
              <span className="muted">({list.length})</span>
            </h2>
            <ul className="finding-list">
              {list.map((f) => {
                const key = findingKey(f)
                return (
                  <FindingCard
                    key={key}
                    finding={f}
                    token={token}
                    reviewId={reviewId}
                    commentInserted={commented.has(key)}
                    applied={applied.has(key)}
                    onCommentInserted={() => onCommentInserted(key)}
                    onApplied={() => onApplied(key)}
                    onIgnored={() => onIgnored(key)}
                  />
                )
              })}
            </ul>
          </section>
        )
      })}
    </div>
  )
}

function PlaybookCheckRow({ check }: { check: PlaybookCheck }) {
  const [navMiss, setNavMiss] = useState(false)
  const loc = checkLocationLabel(check)
  const canNavigate = checkCanNavigate(check)

  async function onNavigate() {
    if (!canNavigate) return
    const ok = await locateAndSelect({
      anchorText: check.anchor_text ?? '',
      occurrenceIndex: check.occurrence_index ?? 0,
    })
    setNavMiss(!ok)
  }

  return (
    <li className={`check-row check-${check.status}`}>
      <div className="finding-head">
        <strong>{check.title || check.text.slice(0, 80)}</strong>
        <span className={`badge badge-${check.status}`}>{checkStatusLabel(check.status)}</span>
        <span className={`badge badge-imp-${check.importance}`}>
          {importanceLabel(check.importance)}
        </span>
      </div>
      {check.reason ? <p className="muted small">{checkReasonLabel(check.reason)}</p> : null}
      {check.evidence?.map((e, i) => <blockquote key={i}>{e.quote}</blockquote>)}
      {check.missing_elements?.length
        ? <p>{t('Бракує: {items}', { items: check.missing_elements.join('; ') })}</p>
        : null}
      {loc ? (
        <button
          type="button"
          className="linkish clause-ref"
          onClick={() => void onNavigate()}
          disabled={!canNavigate}
          title={
            check.insert_mode === 'insert'
              ? t('Рекомендоване місце вставки (не точна локація)')
              : canNavigate
                ? t('Показати в документі')
                : t('Пункт без прив’язки')
          }
        >
          {loc}
        </button>
      ) : null}
      {check.insert_mode === 'insert' && loc ? (
        <p className="muted small">{t('Рекомендоване місце вставки (не точна локація)')}</p>
      ) : null}
      {navMiss ? (
        <p className="warn">{t('Не вдалося знайти пункт — документ змінився?')}</p>
      ) : null}
      <p className="muted small">{check.text}</p>
    </li>
  )
}

function ImportanceChips({
  value,
  counts,
  onChange,
}: {
  value: Importance | 'all'
  counts: Record<Importance, number>
  onChange: (next: Importance | 'all') => void
}) {
  return (
    <div className="filters">
      {IMPORTANCE_ORDER.map((level) => (
        <button
          key={level}
          type="button"
          className={
            value === level ? `chip active chip-imp-${level}` : `chip chip-imp-${level}`
          }
          onClick={() => onChange(value === level ? 'all' : level)}
        >
          {importanceLabel(level)}: {counts[level]}
        </button>
      ))}
    </div>
  )
}

function findingKey(f: Finding): string {
  return `${f.finding_type}:${f.clause_ref ?? f.section_path ?? ''}#${f.rule_id ?? f.order ?? 0}#${f.occurrence_index}`
}

function withKey(prev: Set<string>, key: string): Set<string> {
  const next = new Set(prev)
  next.add(key)
  return next
}

function groupByType(findings: Finding[]): Record<GroupType, Finding[]> {
  const out: Record<GroupType, Finding[]> = {
    deviation: [],
    missing: [],
    atypical: [],
  }
  for (const f of findings) {
    const t: GroupType = f.finding_type in out ? (f.finding_type as GroupType) : 'deviation'
    out[t].push(f)
  }
  return out
}

function normalizeFinding(raw: Finding): Finding {
  return {
    ...raw,
    finding_type: raw.finding_type ?? 'deviation',
    justification: raw.justification ?? '',
    anchor_text: raw.anchor_text ?? '',
    clause_ref: raw.clause_ref ?? null,
    replacement_options: raw.replacement_options ?? [],
  }
}

function normalizePlaybookCheck(raw: PlaybookCheck): PlaybookCheck {
  return {
    ...raw,
    clause_ref: raw.clause_ref ?? null,
    section_path: raw.section_path ?? null,
    anchor_text: raw.anchor_text ?? '',
    occurrence_index: raw.occurrence_index ?? 0,
    insert_mode: raw.insert_mode ?? 'replace',
  }
}

function safeParse(text: string): unknown {
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch (err) {
    console.warn('SSE payload not JSON:', text, err)
    return null
  }
}


function documentText(ooxml: string): string {
  const doc = new DOMParser().parseFromString(ooxml, 'application/xml')
  const body = doc.getElementsByTagNameNS('*', 'body')[0] ?? doc
  return Array.from(body.getElementsByTagNameNS('*', 't')).map(n => n.textContent ?? '').join('\n')
}
